import type { Instance } from "mobx-state-tree";
import { types, flow } from "mobx-state-tree";
import { observable, runInAction } from "mobx";
import type { UserProfile } from "@/domains/pm/types";
import * as pmRepo from "./repository";

/**
 * WHA-3507 — per-project cache of users allowed to be assigned to issues
 * or @-mentioned in comments on that project. Backed by the
 * `get_project_assignable_users` Postgres RPC. Used by the issue assignee
 * pickers and `MentionTextarea` to scope candidates to project members so
 * subcontractors are not offered on projects they cannot see.
 *
 * Backed by MobX `observable.map`/`observable.set` rather than MST volatile
 * arrays so observer components re-render when entries land — a raw Map on
 * a volatile field would mutate silently and views would stay stale.
 */
export const ProjectMembersStore = types
  .model("ProjectMembersStore", {})
  .volatile(() => ({
    cache: observable.map<number, UserProfile[]>(),
    loading: observable.set<number>(),
  }))
  .views((self) => ({
    /**
     * Synchronous cache read. Returns an empty array if the project hasn't
     * been fetched yet — call `fetch(projectId)` to populate it.
     */
    get(projectId: number): UserProfile[] {
      return self.cache.get(projectId) ?? [];
    },
    isLoading(projectId: number): boolean {
      return self.loading.has(projectId);
    },
    /** Whether the cache holds a fetched entry for `projectId`. Callers
     *  that prune state based on the member list should wait until this
     *  returns true; otherwise an empty pre-fetch list would erase valid
     *  selections. */
    has(projectId: number): boolean {
      return self.cache.has(projectId);
    },
    /**
     * Intersection of allowed users across several projects. Used by the
     * bulk Actions menu when the selection spans multiple projects: only
     * users assignable to ALL selected issues' projects should appear.
     * Returns an empty array if any of the requested projects has not
     * been fetched yet (caller is responsible for fetching first).
     */
    intersection(projectIds: number[]): UserProfile[] {
      if (projectIds.length === 0) return [];
      const lists: (UserProfile[] | undefined)[] = projectIds.map((id) => self.cache.get(id));
      if (lists.some((l) => l == null)) return [];
      const [first, ...rest] = lists as UserProfile[][];
      if (rest.length === 0) return first;
      const restIdSets = rest.map((l) => new Set(l.map((u) => u.id)));
      return first.filter((u) => restIdSets.every((s) => s.has(u.id)));
    },
  }))
  .actions((self) => ({
    /**
     * Fetch the assignable user list for `projectId` and cache it. No-ops
     * if the list is already cached or a fetch is in flight. The RPC
     * enforces RLS, so an empty result is a real "no access" answer, not
     * an error — we still cache it to avoid re-fetching.
     */
    fetch: flow(function* fetch(projectId: number) {
      if (!projectId) return;
      if (self.cache.has(projectId)) return;
      if (self.loading.has(projectId)) return;
      runInAction(() => self.loading.add(projectId));
      try {
        const users: UserProfile[] = yield pmRepo.fetchProjectAssignableUsers(projectId);
        runInAction(() => self.cache.set(projectId, users));
      } catch (e) {
        // Best-effort: leave the cache miss in place so a retry is possible
        // and the picker falls back to an empty list (matching what RLS
        // would have surfaced anyway). Logging only — no toast — because
        // the picker mounts on every row open and a transient blip should
        // not spam the user.
        console.error("[ProjectMembersStore.fetch] failed for", projectId, e);
      } finally {
        runInAction(() => self.loading.delete(projectId));
      }
    }),
    /**
     * Drop the cached entry for `projectId` so the next read refetches.
     * Useful after a project membership change (currently unused — the
     * picker mounts long enough that staleness is rarely visible — kept
     * for completeness).
     */
    invalidate(projectId: number) {
      self.cache.delete(projectId);
    },
    invalidateAll() {
      self.cache.clear();
    },
  }));

export type ProjectMembersStoreInstance = Instance<typeof ProjectMembersStore>;
